/**
 * Students landing page.
 *
 * - Hero: editorial title + dominant Galicia photo + "Check availability" CTA.
 * - Pricing: three private 1-on-1 cards (£65 / £50 / £35).
 * - What to expect: 4 highlights, asymmetric grid.
 * - Testimonials placeholder.
 * - Final CTA strip.
 *
 * Existing students with a passcode can still type it via the "I already have
 * a code" disclosure at the very top of the page, which forwards them to the
 * generic /c/:passcode magic-link route.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStudentAuth } from "../StudentAuthContext";

const IMG_HERO =
  "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1600&q=85";
const IMG_DETAIL =
  "https://images.unsplash.com/photo-1495446815901-a7297e633e8d?auto=format&fit=crop&w=1400&q=85";

const TIERS = [
  {
    id: "evening-60",
    duration: "60 min",
    price: "£65",
    title: "Evening lesson",
    blurb: "Private 1:1 · After 4 PM weekdays",
    bullets: ["Tailor-made lesson plan", "Online via your virtual classroom", "Booster sessions on request"],
    accent: false,
    testid: "tier-evening-60",
  },
  {
    id: "daytime-60",
    duration: "60 min",
    price: "£50",
    title: "Daytime lesson",
    blurb: "Private 1:1 · Before 4 PM / school holidays",
    bullets: ["Tailor-made lesson plan", "Online via your virtual classroom", "Flexible weekday slots"],
    accent: true,
    testid: "tier-daytime-60",
  },
  {
    id: "daytime-30",
    duration: "30 min",
    price: "£35",
    title: "Quick lesson",
    blurb: "Private 1:1 · Before 4 PM / school holidays",
    bullets: ["Focused 30-minute drill", "Online via your virtual classroom", "Great for conversation practice"],
    accent: false,
    testid: "tier-daytime-30",
  },
];

const HIGHLIGHTS = [
  {
    label: "Built around you",
    body:
      "100% personalised lesson plans — your level, your goals, the conversations you actually want to have.",
  },
  {
    label: "Flexible scheduling",
    body:
      "Book lessons that fit your week. Daytime, evenings, school holidays — pick what works.",
  },
  {
    label: "Real-world Spanish",
    body:
      "Travel, work, relocation, exam prep, family life — every lesson is anchored in real situations.",
  },
  {
    label: "Friendly tutors",
    body:
      "Native and near-native teachers based in Galicia. Patient, curious, fluent in your goals as well as Spanish.",
  },
];

export default function Students() {
  const navigate = useNavigate();
  const { status, student, logout } = useStudentAuth();
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");

  function gotoBook() {
    navigate("/book");
  }

  function submitCode(e) {
    e.preventDefault();
    const c = code.trim();
    if (c) navigate(`/c/${encodeURIComponent(c)}`);
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-16 md:pt-20"
      data-testid="students-page"
    >
      {/* Top bar: existing-student affordances */}
      <div className="border-b border-[#1C1C1C]/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-wrap items-center justify-between gap-3 text-xs">
          {/* Left: passcode shortcut for one-off magic links */}
          <div className="flex items-center gap-2">
            {!showCode ? (
              <button
                onClick={() => setShowCode(true)}
                className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 hover:text-[#1C1C1C] transition-colors"
                data-testid="students-have-code-toggle"
              >
                Have a lesson passcode? Enter it →
              </button>
            ) : (
              <form onSubmit={submitCode} className="flex items-center gap-2">
                <input
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Lesson passcode"
                  className="px-3 py-1.5 bg-white border border-[#1C1C1C]/15 rounded-md text-sm font-mono tracking-wider focus:outline-none focus:border-[#1C1C1C]/40"
                  data-testid="students-have-code-input"
                />
                <button
                  type="submit"
                  disabled={!code.trim()}
                  className="px-3 py-1.5 bg-[#1C1C1C] text-[#FAF9F6] rounded-md text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40"
                  data-testid="students-have-code-submit"
                >
                  Enter →
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCode(false); setCode(""); }}
                  className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/50 hover:text-[#1C1C1C]/80"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>

          {/* Right: account-level sign-in / my-lessons */}
          <div className="flex items-center gap-4">
            {status === "auth" ? (
              <>
                <span
                  className="hidden sm:inline text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55"
                  data-testid="students-greeting"
                >
                  Hola{student?.name ? `, ${student.name.split(" ")[0]}` : ""}
                </span>
                <Link
                  to="/me"
                  className="px-3 py-1.5 bg-[#1C1C1C] text-[#FAF9F6] rounded-md text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
                  data-testid="students-me-link"
                >
                  My lessons →
                </Link>
                <button
                  onClick={logout}
                  className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/50 hover:text-[#1C1C1C]/80 transition-colors"
                  data-testid="students-signout-btn"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 hidden sm:inline">
                  Already a student?
                </span>
                <Link
                  to="/login"
                  state={{ next: "/me" }}
                  className="px-3 py-1.5 bg-[#1C1C1C] text-[#FAF9F6] rounded-md text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
                  data-testid="students-signin-link"
                >
                  Sign in →
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto pt-12 md:pt-20">
        <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-end">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-6">
              Private lessons · 1-on-1
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tighter font-light text-[#1C1C1C]">
              Spanish,<br />shaped around you.
            </h1>
            <p className="mt-6 text-lg text-[#1C1C1C]/70 leading-relaxed max-w-xl">
              No one-size syllabus. No fixed pace. Just lessons built around how
              you live, the conversations you actually have, and where you want
              Spanish to take you — from Sevilla to Santiago and beyond.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={gotoBook}
                className="px-6 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
                data-testid="students-hero-cta"
              >
                Check availability →
              </button>
              <a
                href="#pricing"
                className="px-6 py-3 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-sm tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors"
                data-testid="students-hero-pricing-link"
              >
                See pricing
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <img
              src={IMG_HERO}
              alt="A student writing in a notebook with a warm coffee"
              loading="eager"
              className="w-full aspect-[4/5] object-cover rounded-2xl md:rounded-3xl"
              draggable={false}
            />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        className="px-6 md:px-10 max-w-7xl mx-auto mt-24 md:mt-32"
        data-testid="students-pricing-section"
      >
        <div className="md:flex md:items-end md:justify-between gap-8 mb-10 md:mb-14">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
              Pricing · 2025/26
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              One student, one teacher, one focus.
            </h2>
          </div>
          <p className="mt-4 md:mt-0 text-base text-[#1C1C1C]/65 max-w-sm md:text-right">
            All lessons are private 1-on-1, online, in your dedicated virtual
            classroom with video, whiteboard, and recording.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {TIERS.map((t) => (
            <div
              key={t.id}
              data-testid={t.testid}
              className={`relative rounded-2xl md:rounded-3xl p-7 md:p-8 flex flex-col ${
                t.accent
                  ? "bg-[#1C1C1C] text-[#FAF9F6]"
                  : "bg-white text-[#1C1C1C] border border-[#1C1C1C]/10"
              }`}
            >
              <div className={`text-[10px] tracking-[0.32em] uppercase font-medium ${t.accent ? "text-[#FAF9F6]/55" : "text-[#1C1C1C]/55"}`}>
                {t.duration}
              </div>
              <div className="mt-4 font-display text-2xl tracking-tight">{t.title}</div>
              <div className={`mt-1 text-sm ${t.accent ? "text-[#FAF9F6]/60" : "text-[#1C1C1C]/60"}`}>
                {t.blurb}
              </div>
              <div className="mt-8 font-display text-5xl md:text-6xl tracking-tighter font-light leading-none">
                {t.price}
              </div>
              <ul className={`mt-8 space-y-2.5 text-sm ${t.accent ? "text-[#FAF9F6]/85" : "text-[#1C1C1C]/75"}`}>
                {t.bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <span className={`mt-1.5 inline-block w-1 h-1 rounded-full ${t.accent ? "bg-[#FAF9F6]/70" : "bg-[#1C1C1C]/60"}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={gotoBook}
                data-testid={`${t.testid}-cta`}
                className={`mt-10 px-5 py-3 rounded-xl text-xs tracking-[0.2em] uppercase font-medium transition-colors ${
                  t.accent
                    ? "bg-[#FAF9F6] text-[#1C1C1C] hover:bg-[#FAF9F6]/90"
                    : "bg-[#1C1C1C] text-[#FAF9F6] hover:bg-[#1C1C1C]/85"
                }`}
              >
                Check availability →
              </button>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sm text-[#1C1C1C]/55">
          Need group lessons or a 6-week course? <a href="mailto:hello@reallanguage.co.uk" className="underline underline-offset-4">Get in touch</a>.
        </p>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto mt-24 md:mt-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-5">
            <img
              src={IMG_DETAIL}
              alt="An open notebook on a sunlit table"
              loading="lazy"
              className="w-full aspect-[4/5] object-cover rounded-2xl md:rounded-3xl"
              draggable={false}
            />
          </div>
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
              What to expect
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Personal,<br />never generic.
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-6 gap-y-8">
              {HIGHLIGHTS.map((h, i) => (
                <div key={h.label} data-testid={`highlight-${i}`}>
                  <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/55 mb-2">
                    0{i + 1} · {h.label}
                  </div>
                  <p className="text-base text-[#1C1C1C]/75 leading-relaxed">{h.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (placeholder) */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto mt-24 md:mt-32">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-6">
          From our students
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {[
            {
              quote:
                "Within a few sessions I was already comfortable holding short conversations on the trip I'd been planning for years.",
              by: "Beta student · Coming soon",
            },
            {
              quote:
                "The lessons feel like real conversations, not drills. I look forward to them every week.",
              by: "Beta student · Coming soon",
            },
          ].map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-${i}`}
              className="bg-white rounded-2xl border border-[#1C1C1C]/10 p-7 md:p-8"
            >
              <blockquote className="font-display text-2xl md:text-3xl leading-snug tracking-tight text-[#1C1C1C] font-light">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-6 text-[10px] tracking-[0.32em] uppercase text-[#1C1C1C]/50 font-medium">
                {t.by}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto mt-24 md:mt-32 pb-24 md:pb-32">
        <div className="bg-[#1C1C1C] text-[#FAF9F6] rounded-2xl md:rounded-3xl p-10 md:p-16 text-center">
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#FAF9F6]/55 mb-4">
            Ready when you are
          </div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tighter font-light max-w-2xl mx-auto">
            Find a slot. Book your first lesson.
          </h2>
          <button
            onClick={gotoBook}
            data-testid="students-final-cta"
            className="mt-10 px-8 py-4 bg-[#FAF9F6] text-[#1C1C1C] rounded-xl text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#FAF9F6]/90 transition-colors"
          >
            Check availability →
          </button>
        </div>
      </section>
    </main>
  );
}
